import React, { useState } from 'react';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Chip from '@mui/joy/Chip';
import List from '@mui/joy/List';
import ListItem from '@mui/joy/ListItem';
import ListItemButton from '@mui/joy/ListItemButton';
import ListItemContent from '@mui/joy/ListItemContent';
import {
  X as CloseRounded,
  ArrowsOut as OpenInFullRounded,
  Robot as SmartToyOutlined,
  Ticket as ConfirmationNumberOutlined,
  Plus as AddRounded,
  ClockCounterClockwise as HistoryRounded,
  ArrowLeft as ArrowBackRounded,
  Trash as DeleteOutlineRounded,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import type { AIChatMessage, SupportTicket } from '../../types/support';
import { useTickets, useAIConversations, useAIConversation, useDeleteAIConversation, aiConversationKeys } from '../../hooks/useSupport';
import { useQueryClient } from '@tanstack/react-query';
import AIChatPanel from './AIChatPanel';
import TicketList from './TicketList';
import TicketDetail from './TicketDetail';
import EscalateToTicketDialog from './EscalateToTicketDialog';

const WELCOME_MESSAGE: AIChatMessage = {
  role: 'assistant',
  content: "Hi! I'm the ApexOS support assistant. I can help you with questions about any feature in the platform — from CRM and billing to marketing and scheduling. What can I help you with today?",
};

interface Props {
  onClose: () => void;
  messages: AIChatMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AIChatMessage[]>>;
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  onNewChat: () => void;
}

export default function SupportChatDrawer({
  onClose,
  messages,
  setMessages,
  conversationId,
  setConversationId,
  onNewChat,
}: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<0 | 1>(0);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [aiContext, setAIContext] = useState<AIChatMessage[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [viewingConversationId, setViewingConversationId] = useState<string | null>(null);

  const { data: ticketsData } = useTickets();
  const ticketCount = ticketsData?.count ?? 0;

  const { data: conversationsData } = useAIConversations();
  const conversations = conversationsData?.results ?? [];

  const { data: viewingConversation } = useAIConversation(viewingConversationId);

  const deleteConversation = useDeleteAIConversation();

  const handleEscalate = (context: AIChatMessage[]) => {
    setAIContext(context);
    setEscalateOpen(true);
  };

  const handleTicketCreated = () => {
    setEscalateOpen(false);
    setActiveTab(1);
  };

  const handleOpenFull = () => {
    navigate('/support');
    onClose();
  };

  const handleLoadConversation = (id: string) => {
    setViewingConversationId(id);
  };

  // When viewing a historical conversation, load its messages
  const handleResumeConversation = () => {
    if (viewingConversation?.messages) {
      setMessages([WELCOME_MESSAGE, ...viewingConversation.messages]);
      setConversationId(viewingConversation.id);
    }
    setViewingConversationId(null);
    setShowHistory(false);
  };

  const handleDeleteConversation = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteConversation.mutate(id, {
      onSuccess: () => {
        if (conversationId === id) {
          onNewChat();
        }
        if (viewingConversationId === id) {
          setViewingConversationId(null);
        }
        queryClient.invalidateQueries({ queryKey: aiConversationKeys.all });
      },
    });
  };

  const handleNewChat = () => {
    onNewChat();
    setShowHistory(false);
    setViewingConversationId(null);
    queryClient.invalidateQueries({ queryKey: aiConversationKeys.all });
  };

  // Determine what to show in the AI tab
  const renderAIContent = () => {
    // Viewing a historical conversation (read-only)
    if (viewingConversationId && viewingConversation) {
      const historyMessages = [WELCOME_MESSAGE, ...(viewingConversation.messages || [])];
      return (
        <Stack sx={{ height: '100%' }}>
          <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <IconButton size="sm" variant="plain" onClick={() => setViewingConversationId(null)}>
              <ArrowBackRounded size={18} />
            </IconButton>
            <Typography level="body-sm" fontWeight={600} sx={{ flex: 1, ml: 0.5 }} noWrap>
              {viewingConversation.title || 'Untitled Chat'}
            </Typography>
            <Button size="sm" variant="soft" color="primary" onClick={handleResumeConversation}>
              Continue
            </Button>
          </Stack>
          <AIChatPanel
            messages={historyMessages}
            setMessages={setMessages}
            conversationId={viewingConversation.id}
            setConversationId={setConversationId}
            onEscalate={handleEscalate}
            compact
          />
        </Stack>
      );
    }

    // History list
    if (showHistory) {
      return (
        <Stack sx={{ height: '100%' }}>
          <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexShrink: 0 }}>
            <IconButton size="sm" variant="plain" onClick={() => setShowHistory(false)}>
              <ArrowBackRounded size={18} />
            </IconButton>
            <Typography level="body-sm" fontWeight={600} sx={{ ml: 0.5 }}>
              Chat History
            </Typography>
          </Stack>
          <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
            {conversations.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography level="body-sm" color="neutral">No previous conversations</Typography>
              </Box>
            ) : (
              <List size="sm" sx={{ py: 0 }}>
                {conversations.map(conv => (
                  <ListItem
                    key={conv.id}
                    endAction={
                      <IconButton
                        size="sm"
                        variant="plain"
                        color="danger"
                        onClick={(e) => handleDeleteConversation(conv.id, e)}
                      >
                        <DeleteOutlineRounded size={16} />
                      </IconButton>
                    }
                  >
                    <ListItemButton
                      onClick={() => handleLoadConversation(conv.id)}
                      selected={conv.id === conversationId}
                      sx={{ borderRadius: 'sm' }}
                    >
                      <ListItemContent>
                        <Typography level="body-sm" fontWeight={600} noWrap>
                          {conv.title || 'Untitled Chat'}
                        </Typography>
                        <Typography level="body-xs" color="neutral">
                          {conv.message_count} messages &middot; {new Date(conv.updated_at).toLocaleDateString()}
                        </Typography>
                      </ListItemContent>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </Stack>
      );
    }

    // Active chat (default)
    return (
      <AIChatPanel
        messages={messages}
        setMessages={setMessages}
        conversationId={conversationId}
        setConversationId={setConversationId}
        onEscalate={handleEscalate}
        compact
      />
    );
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 80,
        right: 24,
        width: { xs: 'calc(100vw - 32px)', sm: 400 },
        height: { xs: 'calc(100vh - 120px)', sm: 560 },
        maxHeight: 'calc(100vh - 120px)',
        zIndex: 1300,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.surface',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 'xl',
        boxShadow: 'xl',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5, bgcolor: '#7013f5', color: 'white', flexShrink: 0 }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <SmartToyOutlined size={20} />
          <Typography level="title-sm" sx={{ color: 'white', fontWeight: 700 }}>
            ApexOS Support
          </Typography>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="sm" variant="plain" sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }} onClick={handleOpenFull}>
            <OpenInFullRounded size={16} />
          </IconButton>
          <IconButton size="sm" variant="plain" sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' } }} onClick={onClose}>
            <CloseRounded size={16} />
          </IconButton>
        </Stack>
      </Stack>

      {/* Custom tab bar */}
      <Stack direction="row" sx={{ flexShrink: 0, borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box
          onClick={() => { setActiveTab(0); setSelectedTicket(null); }}
          sx={{
            flex: 1,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            cursor: 'pointer',
            fontWeight: activeTab === 0 ? 600 : 400,
            fontSize: 'sm',
            borderBottom: activeTab === 0 ? '2px solid' : '2px solid transparent',
            borderColor: activeTab === 0 ? 'primary.500' : 'transparent',
            color: activeTab === 0 ? 'primary.600' : 'text.secondary',
            transition: 'all 0.15s',
            '&:hover': { bgcolor: 'background.level1' },
          }}
        >
          <SmartToyOutlined size={16} />
          Ask AI
        </Box>
        <Box
          onClick={() => { setActiveTab(1); setSelectedTicket(null); }}
          sx={{
            flex: 1,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            cursor: 'pointer',
            fontWeight: activeTab === 1 ? 600 : 400,
            fontSize: 'sm',
            borderBottom: activeTab === 1 ? '2px solid' : '2px solid transparent',
            borderColor: activeTab === 1 ? 'primary.500' : 'transparent',
            color: activeTab === 1 ? 'primary.600' : 'text.secondary',
            transition: 'all 0.15s',
            '&:hover': { bgcolor: 'background.level1' },
          }}
        >
          <ConfirmationNumberOutlined size={16} />
          My Tickets
          {ticketCount > 0 && (
            <Chip size="sm" variant="solid" color="danger" sx={{ minHeight: 18, fontSize: '0.65rem' }}>
              {ticketCount}
            </Chip>
          )}
        </Box>
      </Stack>

      {/* Tab content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
        {activeTab === 0 && (
          <>
            {/* Action bar for AI tab */}
            {!showHistory && !viewingConversationId && (
              <Stack direction="row" spacing={0.5} sx={{ px: 1.5, pt: 1, flexShrink: 0 }}>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<AddRounded />}
                  onClick={handleNewChat}
                  sx={{ flex: 1 }}
                >
                  New Chat
                </Button>
                <Button
                  variant="outlined"
                  color="primary"
                  size="sm"
                  startDecorator={<HistoryRounded />}
                  onClick={() => setShowHistory(true)}
                  sx={{ flex: 1 }}
                >
                  History
                </Button>
              </Stack>
            )}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0 }}>
              {renderAIContent()}
            </Box>
          </>
        )}

        {activeTab === 1 && (
          <>
            {selectedTicket ? (
              <TicketDetail
                ticketId={selectedTicket.id}
                onBack={() => setSelectedTicket(null)}
                compact
              />
            ) : (
              <>
                {/* New ticket button */}
                <Box sx={{ p: 1.5, flexShrink: 0 }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="sm"
                    startDecorator={<AddRounded />}
                    onClick={() => { setAIContext([]); setEscalateOpen(true); }}
                    sx={{ width: '100%' }}
                  >
                    New Ticket
                  </Button>
                </Box>
                {/* Ticket list */}
                <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                  <TicketList
                    onSelectTicket={setSelectedTicket}
                    selectedTicketId={null}
                    compact
                  />
                </Box>
              </>
            )}
          </>
        )}
      </Box>

      <EscalateToTicketDialog
        open={escalateOpen}
        onClose={() => setEscalateOpen(false)}
        aiContext={aiContext}
        onSuccess={handleTicketCreated}
      />
    </Box>
  );
}
